import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect, useMemo } from 'react';

/**
 * Layout Context - Manages global layout state for 3-pane architecture
 *
 * Optimized with split contexts to prevent unnecessary re-renders:
 * - LayoutStateContext: Contains all state values
 * - LayoutActionsContext: Contains all action functions (stable references)
 *
 * State managed:
 * - Active module (inbox, calendar, tasks, contacts, goals)
 * - Active folder (all, unread, starred, etc.)
 * - Selected item ID (for detail pane)
 * - Mobile drawer open/closed state
 * - Selected account ID (for unified vs single account view)
 * - Sidebar collapsed state (for resizable layout)
 * - Folder list collapsed state (for inbox folder list)
 * - List pane width (for resizable layout)
 * - Email grouping preference (date, sender)
 */

export type Module = 'focus' | 'today' | 'inbox' | 'calendar' | 'tasks' | 'contacts' | 'goals' | 'endeavors' | 'weekly-reviews' | 'notes' | 'bookmarks' | 'documents' | 'affinity-diagrams' | 'health' | 'settings' | 'transactions' | 'budget' | 'burn' | 'allocations' | 'investments' | 'loans' | 'angel-investing' | 'finance-calendar' | 'merchants' | 'subscriptions' | 'chat' | 'betabox';
export type EmailGroupBy = 'date' | 'sender' | 'thread';

// Split state and actions for better performance
interface LayoutState {
  // Module state
  activeModule: Module;

  // Folder state (contextual per module)
  activeFolder: string;

  // Selected item (for detail pane)
  selectedItemId: string | null;

  // Mobile drawer state
  isMobileDrawerOpen: boolean;

  // Account selection (unified vs single account)
  selectedAccountId: string | null;

  // Sidebar collapsed state
  isSidebarCollapsed: boolean;

  // Folder list collapsed state
  isFolderListCollapsed: boolean;

  // List pane width (for resizable layout)
  listPaneWidth: number;

  // List pane collapsed state
  isListPaneCollapsed: boolean;

  // Detail pane collapsed state
  isDetailPaneCollapsed: boolean;

  // Email grouping preference
  emailGroupBy: EmailGroupBy;

  // Density mode preference
  densityMode: 'comfortable' | 'compact';
}

interface LayoutActions {
  // Module actions
  setActiveModule: (module: Module) => void;

  // Folder actions
  setActiveFolder: (folder: string) => void;

  // Selected item actions
  setSelectedItemId: (id: string | null) => void;

  // Mobile drawer actions
  openMobileDrawer: () => void;
  closeMobileDrawer: () => void;
  toggleMobileDrawer: () => void;

  // Account selection actions
  setSelectedAccountId: (id: string | null) => void;

  // Sidebar actions
  toggleSidebar: () => void;

  // Folder list actions
  toggleFolderList: () => void;

  // List pane actions
  setListPaneWidth: (width: number) => void;
  toggleListPane: () => void;

  // Detail pane actions
  toggleDetailPane: () => void;

  // Email grouping actions
  setEmailGroupBy: (groupBy: EmailGroupBy) => void;

  // Density mode actions
  setDensityMode: (mode: 'comfortable' | 'compact') => void;
  toggleDensityMode: () => void;

  // Task count refresh action
  refreshTaskCount: () => void;

  // Email count refresh action
  refreshEmailCount: () => void;
}

// Legacy combined interface for backward compatibility
interface LayoutContextValue extends LayoutState, LayoutActions {}

// Split contexts for optimized re-renders
const LayoutStateContext = createContext<LayoutState | undefined>(undefined);
const LayoutActionsContext = createContext<LayoutActions | undefined>(undefined);

interface LayoutProviderProps {
  children: ReactNode;
}

export const LayoutProvider: React.FC<LayoutProviderProps> = ({ children }) => {
  // State declarations
  const [activeModule, setActiveModule] = useState<Module>('inbox');
  const [activeFolder, setActiveFolder] = useState<string>('all');
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);

  // Account selection (null = unified view)
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(() => {
    const saved = localStorage.getItem('concerto_selected_account');
    return saved === 'null' ? null : saved;
  });

  // Sidebar collapsed state
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('concerto_sidebar_collapsed');
    return saved === 'true';
  });

  // Folder list collapsed state
  const [isFolderListCollapsed, setIsFolderListCollapsed] = useState(() => {
    const saved = localStorage.getItem('concerto_folder_list_collapsed');
    return saved === 'true';
  });

  // List pane width (default 360px)
  const [listPaneWidth, setListPaneWidth] = useState(() => {
    const saved = localStorage.getItem('concerto_list_pane_width');
    return saved ? parseInt(saved, 10) : 360;
  });

  // List pane collapsed state
  const [isListPaneCollapsed, setIsListPaneCollapsed] = useState(() => {
    const saved = localStorage.getItem('concerto_list_pane_collapsed');
    return saved === 'true';
  });

  // Detail pane collapsed state
  const [isDetailPaneCollapsed, setIsDetailPaneCollapsed] = useState(() => {
    const saved = localStorage.getItem('concerto_detail_pane_collapsed');
    return saved === 'true';
  });

  // Email grouping preference (default 'thread')
  const [emailGroupBy, setEmailGroupBy] = useState<EmailGroupBy>(() => {
    const saved = localStorage.getItem('concerto_email_group_by');
    return (saved === 'sender' || saved === 'date' || saved === 'thread') ? saved : 'thread';
  });

  // Density mode preference (default 'compact')
  const [densityMode, setDensityMode] = useState<'comfortable' | 'compact'>(() => {
    const saved = localStorage.getItem('concerto_density_mode');
    return (saved === 'comfortable' || saved === 'compact') ? saved : 'compact';
  });

  // Persist account selection
  useEffect(() => {
    localStorage.setItem('concerto_selected_account', selectedAccountId || 'null');
  }, [selectedAccountId]);

  // Persist sidebar state
  useEffect(() => {
    localStorage.setItem('concerto_sidebar_collapsed', String(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  // Persist folder list state
  useEffect(() => {
    localStorage.setItem('concerto_folder_list_collapsed', String(isFolderListCollapsed));
  }, [isFolderListCollapsed]);

  // Persist list pane width
  useEffect(() => {
    localStorage.setItem('concerto_list_pane_width', String(listPaneWidth));
  }, [listPaneWidth]);

  // Persist list pane collapsed state
  useEffect(() => {
    localStorage.setItem('concerto_list_pane_collapsed', String(isListPaneCollapsed));
  }, [isListPaneCollapsed]);

  // Persist detail pane collapsed state
  useEffect(() => {
    localStorage.setItem('concerto_detail_pane_collapsed', String(isDetailPaneCollapsed));
  }, [isDetailPaneCollapsed]);

  // Persist email grouping preference
  useEffect(() => {
    localStorage.setItem('concerto_email_group_by', emailGroupBy);
  }, [emailGroupBy]);

  // Persist density mode preference
  useEffect(() => {
    localStorage.setItem('concerto_density_mode', densityMode);
  }, [densityMode]);

  // Memoized action callbacks (stable references)
  const openMobileDrawer = useCallback(() => setIsMobileDrawerOpen(true), []);
  const closeMobileDrawer = useCallback(() => setIsMobileDrawerOpen(false), []);
  const toggleMobileDrawer = useCallback(() => setIsMobileDrawerOpen(prev => !prev), []);
  const toggleSidebar = useCallback(() => setIsSidebarCollapsed(prev => !prev), []);
  const toggleFolderList = useCallback(() => setIsFolderListCollapsed(prev => !prev), []);
  const toggleListPane = useCallback(() => setIsListPaneCollapsed(prev => !prev), []);
  const toggleDetailPane = useCallback(() => setIsDetailPaneCollapsed(prev => !prev), []);
  const toggleDensityMode = useCallback(() => setDensityMode(prev => prev === 'compact' ? 'comfortable' : 'compact'), []);

  // Task count refresh action - dispatches custom event for LeftSidebar to listen to
  const refreshTaskCount = useCallback(() => {
    window.dispatchEvent(new CustomEvent('refreshTaskCount'));
  }, []);

  // Email count refresh action - dispatches custom event for LeftSidebar to listen to
  const refreshEmailCount = useCallback(() => {
    window.dispatchEvent(new CustomEvent('refreshEmailCount'));
  }, []);

  // Memoize state object to prevent unnecessary re-renders
  const state = useMemo<LayoutState>(() => ({
    activeModule,
    activeFolder,
    selectedItemId,
    isMobileDrawerOpen,
    selectedAccountId,
    isSidebarCollapsed,
    isFolderListCollapsed,
    listPaneWidth,
    isListPaneCollapsed,
    isDetailPaneCollapsed,
    emailGroupBy,
    densityMode,
  }), [
    activeModule,
    activeFolder,
    selectedItemId,
    isMobileDrawerOpen,
    selectedAccountId,
    isSidebarCollapsed,
    isFolderListCollapsed,
    listPaneWidth,
    isListPaneCollapsed,
    isDetailPaneCollapsed,
    emailGroupBy,
    densityMode,
  ]);

  // Memoize actions object (stable reference since callbacks are memoized)
  const actions = useMemo<LayoutActions>(() => ({
    setActiveModule,
    setActiveFolder,
    setSelectedItemId,
    openMobileDrawer,
    closeMobileDrawer,
    toggleMobileDrawer,
    setSelectedAccountId,
    toggleSidebar,
    toggleFolderList,
    setListPaneWidth,
    toggleListPane,
    toggleDetailPane,
    setEmailGroupBy,
    setDensityMode,
    toggleDensityMode,
    refreshTaskCount,
    refreshEmailCount,
  }), [
    openMobileDrawer,
    closeMobileDrawer,
    toggleMobileDrawer,
    toggleSidebar,
    toggleFolderList,
    toggleListPane,
    toggleDetailPane,
    toggleDensityMode,
    refreshTaskCount,
    refreshEmailCount,
  ]);

  return (
    <LayoutStateContext.Provider value={state}>
      <LayoutActionsContext.Provider value={actions}>
        {children}
      </LayoutActionsContext.Provider>
    </LayoutStateContext.Provider>
  );
};

// New optimized hooks for accessing state and actions separately
export const useLayoutState = (): LayoutState => {
  const context = useContext(LayoutStateContext);
  if (!context) {
    throw new Error('useLayoutState must be used within a LayoutProvider');
  }
  return context;
};

export const useLayoutActions = (): LayoutActions => {
  const context = useContext(LayoutActionsContext);
  if (!context) {
    throw new Error('useLayoutActions must be used within a LayoutProvider');
  }
  return context;
};

// Legacy hook for backward compatibility - combines state and actions
// Components should migrate to useLayoutState() and useLayoutActions() for better performance
export const useLayout = (): LayoutContextValue => {
  const state = useLayoutState();
  const actions = useLayoutActions();

  // Memoize the combined object to prevent unnecessary re-renders
  return useMemo(() => ({
    ...state,
    ...actions,
  }), [state, actions]);
};

